import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authAPI } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight } from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get("expired") === "1";
  const [mode, setMode] = useState("choice");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    authAPI.me()
      .then(() => navigate("/", { replace: true }))
      .catch(() => {});
  }, [navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Email et mot de passe requis"); return; }
    setLoading(true);
    try {
      await authAPI.login(email, password);
      const me = await authAPI.me();
      navigate("/", { replace: true, state: { user: me.data } });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Identifiants incorrects");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1770009971150-f50bc7d373a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwd2hpdGUlMjBnZW9tZXRyaWMlMjBhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZSUyMHNoYXBlc3xlbnwwfHx8fDE3NzEyNjg4ODR8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/80 flex items-center justify-center">
          <div className="text-center px-12">
            <img src="/logo_komptai_dark.svg" alt="KomptAI" className="w-72 mx-auto mb-4" />
            <p className="text-lg text-slate-300 max-w-md" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              Gérez vos clients, fournisseurs et produits depuis une seule plateforme.
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 bg-white">
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center lg:text-left">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <span className="lg:hidden"><img src="/logo_komptai_light.svg" alt="KomptAI" className="w-48 inline-block mb-2" /><br /></span>
              Connexion
            </h2>
            <p className="text-slate-500">
              Connectez-vous pour accéder à votre espace de gestion.
            </p>
          </div>

          {sessionExpired && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="session-expired-banner">
              <Lock size={16} className="mt-0.5 shrink-0" />
              <span>Session expirée pour inactivité. Reconnectez-vous.</span>
            </div>
          )}

          {mode === "choice" && (
            <div className="space-y-3">
              <Button
                onClick={handleGoogleLogin}
                className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-md uppercase text-xs font-bold tracking-wider transition-all hover:shadow-md hover:-translate-y-[1px]"
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continuer avec Google
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
                <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-3 text-slate-400">ou</span></div>
              </div>

              <Button
                variant="outline"
                onClick={() => setMode("password")}
                className="w-full h-12 rounded-md uppercase text-xs font-bold tracking-wider border-slate-300 hover:bg-slate-50"
                data-testid="email-login-btn"
              >
                <Mail size={18} className="mr-3" />
                Se connecter avec email
              </Button>
            </div>
          )}

          {mode === "password" && (
            <form onSubmit={handlePasswordLogin} className="space-y-4">
              <div>
                <Label>Email</Label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="votre@email.fr" className="pl-10 h-11"
                    autoFocus data-testid="login-email-input"
                  />
                </div>
              </div>
              <div>
                <Label>Mot de passe</Label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="password" value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="Votre mot de passe" className="pl-10 h-11"
                    data-testid="login-password-input"
                  />
                </div>
              </div>
              <Button
                type="submit" disabled={loading}
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md uppercase text-xs font-bold tracking-wider"
                data-testid="login-submit-btn"
              >
                {loading ? "Connexion..." : <><ArrowRight size={16} className="mr-2" /> Se connecter</>}
              </Button>
              <button type="button" onClick={() => setMode("choice")} className="w-full text-center text-sm text-slate-500 hover:text-slate-700" data-testid="back-to-choice-btn">
                Retour
              </button>
            </form>
          )}

          <p className="text-center text-xs text-slate-400">
            En vous connectant, vous acceptez nos conditions d'utilisation.
          </p>
        </div>
      </div>
    </div>
  );
}
